/*
<summary>
   MultiSelectWithChips wraps react-multi-select-component's MultiSelect with a
   placeholder-only value renderer and a removable-chip list of the current selection below it
   - the same pattern add-edit-base-user-form.tsx used inline for its Process picker, extracted
   here so it can be reused wherever a chip-style multi-select is needed. It's a plain
   controlled component (value/onChange), not Formik-aware itself.
   FormikMultiSelectWithChips (default export) adapts it for use as a Formik field via
   <Field name="X" as={FormikMultiSelectWithChips} />, reading/writing the field's value
   through useField the same way the other FormikForm* components do, plus the standard
   FormikFormErrorHandler for validation messages.
   Developer: Aashish Singh, Created Date: 07-Sep-2026
</summary>
<param>name: Formik field name, options: full option list, placeholder: translation key,
disabled: optional disabled flag, intl: injected by injectIntl for FormikFormErrorHandler</param>
<returns>Returns JSX</returns>
*/

import React from 'react';
import { injectIntl } from 'react-intl';
import { useField } from 'formik';
import { MultiSelect } from 'react-multi-select-component';
import { IMultiSelectOption } from '../../models/ICommon';
import { formatMessage } from '../../translations/format-message';
import FormikFormErrorHandler from './FormikFormErrorHandler';

interface IMultiSelectWithChipsProps {
  options: IMultiSelectOption[];
  value: IMultiSelectOption[];
  onChange: (selected: IMultiSelectOption[]) => void;
  placeholder: string;
  disabled?: boolean;
  className?: string;
}

export const MultiSelectWithChips = ({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  className,
}: IMultiSelectWithChipsProps) => {
  const removeOption = (optionToRemove: IMultiSelectOption) => {
    onChange(value.filter((option) => option.value !== optionToRemove.value));
  };

  const CustomValueRenderer = () => (
    <span className="text-muted">{formatMessage(placeholder)}</span>
  );

  return (
    <div className={className}>
      <MultiSelect
        options={options}
        value={value}
        onChange={onChange}
        labelledBy={formatMessage(placeholder)}
        hasSelectAll={true}
        valueRenderer={CustomValueRenderer}
        disabled={disabled}
      />
      {value.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mt-3 process-info">
          {value.map((item) => (
            <span
              key={item.value}
              className="badge bg-info d-flex align-items-center cursor-pointer"
            >
              {item.label}
              {!disabled && (
                <svg
                  onClick={() => removeOption(item)}
                  fill="none"
                  height="20"
                  viewBox="0 0 24 24"
                  width="20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <clipPath id={`multi-select-chip-clip-${item.value}`}>
                    <path d="m0 0h24v24h-24z" />
                  </clipPath>
                  <g clipPath={`url(#multi-select-chip-clip-${item.value})`}>
                    <path
                      d="m18.3 5.71c-.39-.39-1.02-.39-1.41 0l-4.89 4.88-4.89003-4.89c-.39-.39-1.02-.39-1.41 0s-.39 1.02 0 1.41l4.89003 4.89-4.89003 4.89c-.39.39-.39 1.02 0 1.41s1.02.39 1.41 0l4.89003-4.89 4.89 4.89c.39.39 1.02.39 1.41 0s.39-1.02 0-1.41l-4.89-4.89 4.89-4.89c.38-.38.38-1.02 0-1.4z"
                      fill="#3c64d0"
                    />
                  </g>
                </svg>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

interface IProps {
  name: string;
  options: IMultiSelectOption[];
  placeholder: string;
  disabled?: boolean;
  intl: any;
}

const FormikMultiSelectWithChips = (props: IProps) => {
  const [field, meta, helpers] = useField({ name: props.name });

  return (
    <>
      <MultiSelectWithChips
        options={props.options}
        value={field.value || []}
        onChange={(selected) => helpers.setValue(selected)}
        placeholder={props.placeholder}
        disabled={props.disabled}
      />
      <FormikFormErrorHandler
        intl={props.intl}
        meta={meta}
      />
    </>
  );
};

export default injectIntl(FormikMultiSelectWithChips);
